import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../store/globalState";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button, HomeAddressIcon, Layout } from "lola-framework-ui-test";
import { FloatingField } from "../components/FloatingField";
import { AuraLayout } from "../components/AuraLayout";
import { APP_ROUTES, TITLE_COLOR } from "../constants";
import { defaultCardOriginCountryCode, resolveCardOriginCountryCodes } from "../constants/countries";
import { useSessionStore } from "../store/useSessionStore";
import { useRouteWithToken } from "../hooks/useRouteWithToken";
import { useClaimLink } from "../hooks/useClaimLink";
import { useVisualViewportPin } from "../hooks/useVisualViewportPin";
import type { CardStepFields } from "./AddCardPage";
import { getEvents, safeCapture } from "../analytics";
import { countryDisplayName } from "../utils/country-display";
import { statesForCountry } from "../utils/states";
import {
  loadZipLookup,
  zipLookupKey,
  type ZipLookup,
} from "../utils/zip-lookup";
import { ScrollFadeIndicator } from "../components/ScrollFadeIndicator";
import { Navbar } from "../components/Navbar";


export interface AddressFormFields {
  street1: string;
  street2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
}

interface AddressFormErrors {
  street1?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface AddCardFullState {
  card?: CardStepFields;
  address: AddressFormFields;
}

export const BillingAddressPage = memo(() => {
  useClaimLink();
  useVisualViewportPin();
  const { theme } = useStore();
  const { session } = useSessionStore();
  const { redirectTo } = useRouteWithToken();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    safeCapture(getEvents().BILLING_VIEW);
  }, []);

  const cardOriginPolicy =
    session.data?.config?.cardPolicy?.cardOriginPolicy ?? "us_only";
  const cardOriginCountryCodes = useMemo(
    () =>
      resolveCardOriginCountryCodes(
        session.data?.config?.corridors,
        cardOriginPolicy
      ),
    [cardOriginPolicy, session.data?.config?.corridors]
  );
  const lockedCountryCode =
    cardOriginCountryCodes.length === 1 ? cardOriginCountryCodes[0] : "";
  const isCountryLocked = Boolean(lockedCountryCode);
  const initialCountryCode = defaultCardOriginCountryCode(
    cardOriginCountryCodes
  );

  const cardData = (location.state as { card?: CardStepFields })?.card;

  useEffect(() => {
    if (!cardData) {
      redirectTo(APP_ROUTES.ADD_CARD_PAGE, { replace: true });
    }
  }, [cardData, redirectTo]);

  const [form, setForm] = useState<AddressFormFields>({
    street1: "",
    street2: "",
    city: "",
    state: "",
    zipCode: "",
    country: initialCountryCode,
  });
  const [errors, setErrors] = useState<AddressFormErrors>({});

  const selectedCountryCode = isCountryLocked
    ? lockedCountryCode
    : form.country;
  const isUS = selectedCountryCode === "US";
  const stateOptions = useMemo(
    () => statesForCountry(selectedCountryCode),
    [selectedCountryCode]
  );

  /**
   * US billing only asks for street and ZIP: the dataset resolves city and
   * state, so the payload keeps its shape without the two extra fields. Other
   * countries have no equivalent dataset and keep collecting them by hand.
   */
  const [zipLookup, setZipLookup] = useState<ZipLookup | null>(null);

  useEffect(() => {
    if (!isUS) return;
    let active = true;
    loadZipLookup()
      .then((lookup) => {
        if (active) setZipLookup(() => lookup);
      })
      .catch(() => {
        // `installChunkReload` reloads on preload failures. Until then the CTA
        // stays disabled rather than submitting an address with no city.
      });
    return () => {
      active = false;
    };
  }, [isUS]);

  const zipLocation = useMemo(() => {
    if (!isUS || !zipLookup) return null;
    const key = zipLookupKey(form.zipCode);
    return key ? zipLookup(key) : null;
  }, [form.zipCode, isUS, zipLookup]);

  const resolvedCity = isUS ? (zipLocation?.city ?? "") : form.city;
  const resolvedState = isUS ? (zipLocation?.stateCode ?? "") : form.state;
  const canSubmit = isUS
    ? Boolean(form.street1.trim()) && Boolean(zipLocation)
    : true;
  const zipUnknown =
    isUS &&
    Boolean(zipLookup) &&
    Boolean(zipLookupKey(form.zipCode)) &&
    !zipLocation;

  const colorsConfig = theme?.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy } = colorsConfig;
  const fieldColors = {
    background: colorsConfig.background,
    title: colorsConfig.title,
    bodyCopy,
    inactiveColor: colorsConfig.subtitile,
    errorColor: colorsConfig.errorColor,
  };

  const handleChange = (field: keyof AddressFormFields, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "country" && value !== prev.country) {
        next.state = "";
      }
      return next;
    });
    if (errors[field as keyof AddressFormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): AddressFormErrors => {
    const errs: AddressFormErrors = {};
    if (!form.street1.trim()) errs.street1 = t("billingAddress.streetRequired");
    if (!isUS) {
      if (!form.city.trim()) errs.city = t("billingAddress.cityRequired");
      if (!form.state.trim()) errs.state = t("billingAddress.stateRequired");
    }

    if (isUS) {
      if (!/^\d{5}(-\d{4})?$/.test(form.zipCode))
        errs.zipCode = t("billingAddress.zipRequired");
      else if (!zipLocation) errs.zipCode = t("billingAddress.zipNotFound");
    } else if (!form.zipCode.trim() || form.zipCode.trim().length < 2) {
      errs.zipCode = t("billingAddress.postalCodeRequired");
    }

    if (
      !selectedCountryCode ||
      !cardOriginCountryCodes.includes(selectedCountryCode)
    ) {
      errs.country = t("billingAddress.countryRequired");
    }

    return errs;
  };

  const handleSubmit = (event?: React.FormEvent) => {
    event?.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    redirectTo(APP_ROUTES.ADD_CARD_PROCESSING_PAGE, {
      state: {
        card: cardData,
        address: {
          ...form,
          city: resolvedCity,
          state: resolvedState,
          country: selectedCountryCode,
        },
      } satisfies AddCardFullState,
    });
  };
  const saveArmed = useRef(false);
  const pressSave = (event?: React.MouseEvent<HTMLButtonElement>) => {
    const fromKeyboard = event?.detail === 0;
    if (!fromKeyboard && !saveArmed.current) return;
    saveArmed.current = false;
    handleSubmit();
  };

  if (!cardData) return null;

  const errorStyle: React.CSSProperties = {
    color: "#dc251c",
    fontSize: "12px",
    marginTop: "4px",
  };

  return (
    <AuraLayout colorConfig={colorsConfig}>
      <Layout.Header>
        <Navbar
          align="center"
          color={TITLE_COLOR}
          title={t("billingAddress.title")}
          onBackClick={() => navigate(-1)}
        />
      </Layout.Header>

      <Layout.Content className="collect">
        <ScrollFadeIndicator color={bodyCopy} />
        <form
          id="billing-address-form"
          className="mock-card-form"
          autoComplete="on"
          onSubmit={handleSubmit}
        >
          <section className="mock-card-fields">
            <div className="mock-card-field">
              <FloatingField
                label={t("billingAddress.fields.street1")}
                value={form.street1}
                required
                isValid={!errors.street1}
                colors={fieldColors}
                icon={
                  <HomeAddressIcon
                    colors={colorsConfig.dropzoneColors}
                    size={18}
                  />
                }
              >
                <input
                  id="billing-street1"
                  style={{ color: bodyCopy }}
                  name="street1"
                  autoComplete="address-line1"
                  value={form.street1}
                  onChange={(e) => handleChange("street1", e.target.value)}
                  autoFocus
                />
              </FloatingField>
              {errors.street1 && <div style={errorStyle}>{errors.street1}</div>}
            </div>

            {!isUS && (
              <>
                <div className="mock-card-field">
                  <FloatingField
                    label={t("billingAddress.fields.state")}
                    value={form.state}
                    required
                    isValid={!errors.state}
                    colors={fieldColors}
                  >
                    {stateOptions.length > 0 ? (
                      <select
                        id="billing-state"
                        style={{ color: bodyCopy }}
                        value={form.state}
                        onChange={(e) => handleChange("state", e.target.value)}
                      >
                        <option value="" />
                        {stateOptions.map((state) => (
                          <option key={state.fullCode} value={state.code}>
                            {state.name}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        id="billing-state"
                        style={{ color: bodyCopy }}
                        name="state"
                        autoComplete="address-level1"
                        value={form.state}
                        onChange={(e) => handleChange("state", e.target.value)}
                      />
                    )}
                  </FloatingField>
                  {errors.state && (
                    <div style={errorStyle}>{errors.state}</div>
                  )}
                </div>

                <div className="mock-card-field">
                  <FloatingField
                    label={t("billingAddress.fields.city")}
                    value={form.city}
                    required
                    isValid={!errors.city}
                    colors={fieldColors}
                  >
                    <input
                      id="billing-city"
                      style={{ color: bodyCopy }}
                      name="city"
                      autoComplete="address-level2"
                      value={form.city}
                      onChange={(e) => handleChange("city", e.target.value)}
                    />
                  </FloatingField>
                  {errors.city && <div style={errorStyle}>{errors.city}</div>}
                </div>
              </>
            )}

            <div className="mock-card-field">
              <FloatingField
                label={t("billingAddress.fields.postalCode")}
                value={form.zipCode}
                required
                isValid={!errors.zipCode}
                colors={fieldColors}
              >
                <input
                  id="billing-postal"
                  style={{ color: bodyCopy }}
                  name="postalCode"
                  autoComplete="postal-code"
                  inputMode={isUS ? "numeric" : "text"}
                  maxLength={10}
                  value={form.zipCode}
                  onChange={(e) =>
                    handleChange(
                      "zipCode",
                      isUS
                        ? e.target.value.replace(/[^\d-]/g, "")
                        : e.target.value
                    )
                  }
                />
              </FloatingField>
              {(errors.zipCode || zipUnknown) && (
                <div style={errorStyle}>
                  {errors.zipCode ?? t("billingAddress.zipNotFound")}
                </div>
              )}
            </div>

            {!isCountryLocked && (
              <div className="mock-card-field">
                <FloatingField
                  label={t("billingAddress.fields.countryCode")}
                  value={form.country}
                  required
                  isValid={!errors.country}
                  colors={fieldColors}
                >
                  <select
                    id="billing-country"
                    style={{ color: bodyCopy }}
                    value={form.country}
                    onChange={(e) => handleChange("country", e.target.value)}
                  >
                    {cardOriginCountryCodes.map((countryCode) => (
                      <option key={countryCode} value={countryCode}>
                        {countryDisplayName(
                          countryCode,
                          i18n.language || "en"
                        )}
                      </option>
                    ))}
                  </select>
                </FloatingField>
                {errors.country && (
                  <div style={errorStyle}>{errors.country}</div>
                )}
              </div>
            )}
          </section>
        </form>
      </Layout.Content>

      <Layout.Footer>
        <section className="valid-cards">
          <section className="footer-section">
            <Button
              showIcon={false}
              size="large"
              disabled={!canSubmit}
              color={textColorBtn}
              background={backgroundBtn}
              onPointerDown={() => {
                saveArmed.current = true;
              }}
              onClick={pressSave}
            >
              {t("billingAddress.continue")}
            </Button>
          </section>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});
